import { useId, useMemo } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { cn } from "../../../lib/utils.js";
import { ChartTooltipCard } from "./ChartTooltip.js";
import { useChartTheme, useEntryAnimation } from "./chart-theme.js";

export interface TrendPoint {
  label: string;
  value: number;
}

export interface TrendAreaProps {
  data: TrendPoint[];
  formatValue: (n: number) => string;
  /** Compact axis-tick formatter (defaults to `formatValue`). */
  formatTick?: (n: number) => string;
  /** Plot height in px; the width tracks the container. */
  height?: number;
  emptyMessage?: string;
  className?: string;
}

/**
 * One measure over time: a Recharts area with its line on top.
 *
 * The Recharts counterpart to the hand-drawn `LineChart`, so a trend sits in
 * the same system as `CategoryDonut` / `CategoryBars` -- same palette, same
 * tooltip card, same entry animation on first paint only.
 *
 * The fill is a vertical fade of the series colour rather than a flat block:
 * it reads as the area under a line, and keeps the gridlines legible through it.
 */
export function TrendArea({
  data,
  formatValue,
  formatTick,
  height = 260,
  emptyMessage = "No data for this range",
  className,
}: TrendAreaProps) {
  const theme = useChartTheme();
  const entry = useEntryAnimation();
  // Scoped per instance: two trends on one page must not share a <defs> id.
  const gradientId = `trend-fill-${useId().replace(/:/g, "")}`;
  const tick = formatTick ?? formatValue;

  const hasData = useMemo(() => data.some(d => Number.isFinite(d.value)), [data]);
  if (data.length === 0 || !hasData) {
    return <div className={cn("py-10 text-center text-[13px] text-surface-400", className)}>{emptyMessage}</div>;
  }

  const color = theme.series[0]!;

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          {/* Horizontal rules only: the x axis is time, which reads along the
              marks themselves, so vertical rules would only add noise. */}
          <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: theme.grid }}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            // Thins the labels itself rather than letting them collide.
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            width={56}
            tickLine={false}
            axisLine={false}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            tickFormatter={v => tick(Number(v ?? 0))}
          />
          <Tooltip
            isAnimationActive={false}
            cursor={{ stroke: theme.axis, strokeDasharray: "3 3", strokeOpacity: 0.5 }}
            wrapperStyle={{ outline: "none" }}
            content={({ active, payload, label }) => {
              const row = active && payload?.length ? payload[0] : undefined;
              if (!row) return null;
              return (
                <ChartTooltipCard
                  title={String(label ?? "")}
                  rows={[{ name: "Total", value: formatValue(Number(row.value ?? 0)), swatchClass: "bg-[var(--chart-1)]" }]}
                />
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            // Drawn in from the left on first paint, then updates instantly.
            isAnimationActive={entry.active}
            animationBegin={0}
            animationDuration={entry.duration}
            animationEasing="ease-out"
            onAnimationEnd={entry.onEnd}
            dot={false}
            activeDot={{ r: 4, stroke: theme.surface, strokeWidth: 2, fill: color }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
